import { useCallback, useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<{ outcome: "accepted" | "dismissed" }>;
}

declare global {
  interface Window {
    /** Set by an inline script in index.html before React mounts */
    __pwaInstallPrompt: BeforeInstallPromptEvent | null;
  }
}

/**
 * Exposes the browser's PWA install prompt. The `beforeinstallprompt` event
 * is captured by an inline script in index.html (before React loads) and
 * stored on `window.__pwaInstallPrompt` so it isn't lost.
 */
export function useInstallPrompt() {
  const [promptEvent, setPromptEvent] =
    useState<BeforeInstallPromptEvent | null>(window.__pwaInstallPrompt);
  const [isInstalled, setIsInstalled] = useState(
    window.matchMedia("(display-mode: standalone)").matches
  );

  useEffect(() => {
    if (isInstalled) {
      return;
    }

    // If the event hasn't fired yet, listen for it
    const handlePrompt = (e: Event) => {
      e.preventDefault();
      window.__pwaInstallPrompt = e as BeforeInstallPromptEvent;
      setPromptEvent(e as BeforeInstallPromptEvent);
    };

    const handleInstalled = () => {
      setIsInstalled(true);
      setPromptEvent(null);
      window.__pwaInstallPrompt = null;
    };

    window.addEventListener("beforeinstallprompt", handlePrompt);
    window.addEventListener("appinstalled", handleInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handlePrompt);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, [isInstalled]);

  const install = useCallback(async () => {
    if (!promptEvent) {
      return;
    }

    const { outcome } = await promptEvent.prompt();
    if (outcome === "accepted") {
      setIsInstalled(true);
    }
    setPromptEvent(null);
    window.__pwaInstallPrompt = null;
  }, [promptEvent]);

  return {
    /** True if the prompt is available and the app is not yet installed */
    canInstall: !!promptEvent && !isInstalled,
    /** Trigger the browser's install dialog */
    install,
    /** True if the app is running as an installed PWA */
    isInstalled
  };
}
